import { Fragment, useEffect, useState } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { toast } from "sonner";
import { Search, UserMinus, History, Flag, X, Trash2, MailPlus } from "lucide-react";
import { ReferralPipeline } from "../components/admin/ReferralPipeline";
import { RMAssessmentReview } from "../components/admin/RMAssessmentReview";
import { RMActivityReport } from "../components/admin/RMActivityReport";
import { RMEmailLog } from "../components/admin/RMEmailLog";
import { RMTrainingOverview } from "../components/admin/RMTrainingOverview";
import { RMQuestionBoard } from "../components/admin/RMQuestionBoard";
import { AddRMForm } from "../components/admin/AddRMForm";

const STATUS_LABELS = {
  active: ["Active", "bg-emerald-500/10 text-emerald-300"],
  paused: ["Paused", "bg-amber-500/10 text-amber-300"],
  non_responsive: ["Non-Responsive", "bg-red-500/10 text-red-300"],
  not_ready: ["Not Ready to Raise", "bg-neutral-500/10 text-neutral-300"],
  other: ["Other", "bg-sky-500/10 text-sky-300"],
};
const SERVICE_LABELS = {
  financial_analyst_support: "Financial Analyst Support",
  masterclass: "Masterclass",
  training_cohort: "Training Cohort",
  monthly_webinar: "Monthly Webinar",
};
const fmt = (iso) => (iso ? iso.slice(0, 10) : "—");

function FlagAlerts() {
  const [flags, setFlags] = useState([]);
  useEffect(() => {
    api.get("/admin/rm-status-flags")
      .then((r) => setFlags(r.data.flags || []))
      .catch(() => {});
  }, []);
  const ack = async (flagId) => {
    try {
      await api.post(`/admin/rm-status-flags/${flagId}/ack`);
      setFlags((f) => f.filter((x) => x.flag_id !== flagId));
    } catch (_) { toast.error("Couldn't dismiss the alert"); }
  };
  if (flags.length === 0) return null;
  return (
    <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="rm-flag-alerts">
      <p className="flex items-center gap-2 text-sm font-bold text-amber-300">
        <Flag size={15} /> Status alerts — clients moved off Active ({flags.length})
      </p>
      <div className="mt-2 space-y-2">
        {flags.map((f) => {
          const [label] = STATUS_LABELS[f.to_status] || [f.to_status];
          return (
            <div key={f.flag_id} className="flex items-start justify-between gap-3 rounded-lg border border-amber-500/20 bg-neutral-950 px-3 py-2" data-testid={`rm-flag-${f.flag_id}`}>
              <div>
                <p className="text-sm text-neutral-100">
                  <b>{f.client_name}</b> · {f.company_name} — <span className="text-emerald-400">Active</span> → <span className="font-bold text-amber-300">{label}</span>
                </p>
                <p className="text-[11px] text-neutral-400">{fmt(f.created_at)} · flagged by {f.rm_name}{f.reason ? ` · Reason: ${f.reason}` : ""}</p>
              </div>
              <button onClick={() => ack(f.flag_id)}
                className="flex shrink-0 items-center gap-1 rounded-md border border-neutral-700 px-2 py-1 text-[10px] font-bold text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
                data-testid={`rm-flag-ack-${f.flag_id}`}>
                <X size={11} /> Dismiss
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssignSearch({ rm, onAssigned }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/rms/founder-search", { params: { q } })
        .then((r) => setResults(r.data.results || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const assign = async (companyId) => {
    setBusy(true);
    try {
      await api.post(`/admin/rms/${rm.user_id}/assign`, { company_id: companyId });
      toast.success(`Assigned to ${rm.name}`);
      setQ(""); setResults([]);
      onAssigned();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't assign"); }
    setBusy(false);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2">
        <Search size={14} className="text-neutral-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={`Search founders / companies to assign to ${rm.name}…`}
          className="w-full bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-500"
          data-testid="rm-assign-search" />
      </div>
      {results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-900 shadow-xl" data-testid="rm-assign-results">
          {results.map((r) => (
            <div key={r.company_id} className="flex items-center justify-between border-b border-neutral-800 px-3 py-2 last:border-0">
              <div>
                <p className="text-sm font-semibold text-neutral-100">{r.company_name}</p>
                <p className="text-[11px] text-neutral-500">{r.founder_name} · {r.founder_email}</p>
                {r.assigned_rm_name && (
                  <p className="text-[10px] font-semibold text-amber-400">Currently with {r.assigned_rm_name}</p>
                )}
              </div>
              <button onClick={() => assign(r.company_id)} disabled={busy || r.assigned_rm_id === rm.user_id}
                className="rounded-md bg-[#0A8B85] px-3 py-1 text-[11px] font-bold text-white hover:bg-[#097a74] disabled:opacity-40"
                data-testid={`rm-assign-btn-${r.company_id}`}>
                {r.assigned_rm_id === rm.user_id ? "Assigned" : r.assigned_rm_name ? "Reassign" : "Assign"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRows({ companyId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/rm/clients/${companyId}/checkins`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [companyId]);
  if (items === null) return <p className="px-4 py-2 text-xs text-neutral-500">Loading…</p>;
  if (items.length === 0) return <p className="px-4 py-2 text-xs text-neutral-500">No check-ins logged yet.</p>;
  return (
    <div className="space-y-1.5 px-4 py-3" data-testid={`admin-rm-history-${companyId}`}>
      {items.map((c) => (
        <div key={c.checkin_id} className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2">
          <p className="text-[11px] font-semibold text-neutral-400">{c.checkin_date || fmt(c.created_at)} · {c.rm_name}</p>
          {c.note && <p className="mt-0.5 whitespace-pre-wrap text-xs text-neutral-300">{c.note}</p>}
          {c.action_items && (
            <p className="mt-1 text-xs text-neutral-300">
              <span className={`mr-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                c.action_items_assigned_to === "client" ? "bg-teal-500/10 text-teal-300" : "bg-sky-500/10 text-sky-300"}`}>
                {c.action_items_assigned_to === "client" ? "Client" : "RM"}
              </span>
              <span className="font-semibold text-neutral-500">Action items:</span> {c.action_items}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminRMs() {
  const [rms, setRms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openHistory, setOpenHistory] = useState({});
  const [tab, setTab] = useState("managers");

  const loadRms = async () => {
    try {
      const { data } = await api.get("/admin/rms");
      setRms(data.rms || []);
      setSelected((s) => s || (data.rms || [])[0] || null);
    } catch (_) { toast.error("Couldn't load RMs"); }
    setLoading(false);
  };
  useEffect(() => { loadRms(); }, []);

  const loadClients = async (rm) => {
    if (!rm) return;
    try {
      const { data } = await api.get(`/admin/rms/${rm.user_id}/clients`);
      setClients(data.rows || []);
    } catch (_) { toast.error("Couldn't load clients"); }
  };
  useEffect(() => { loadClients(selected); }, [selected]);

  const unassign = async (companyId) => {
    if (!window.confirm("Remove this client from the RM's book?")) return;
    try {
      await api.delete(`/admin/rms/assignments/${companyId}`);
      toast.success("Client unassigned");
      loadClients(selected); loadRms();
    } catch (_) { toast.error("Couldn't unassign"); }
  };

  const removeRm = async (rm) => {
    if (!window.confirm(`Remove ${rm.name} (${rm.email}) as an RM? Their ${rm.client_count} client(s) will be unassigned.`)) return;
    try {
      await api.delete(`/admin/rms/${rm.user_id}`);
      toast.success(`${rm.name} removed`);
      if (selected?.user_id === rm.user_id) setSelected(null);
      loadRms();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't remove RM"); }
  };

  const resendWelcome = async (rm) => {
    if (!window.confirm(`Resend the welcome email to ${rm.email}? A fresh temporary password will be issued and the old one stops working.`)) return;
    try {
      const { data } = await api.post(`/admin/rms/${rm.user_id}/resend-welcome`);
      toast.success(data.welcome_email_sent ? `Welcome email resent to ${data.email}` : "Password reset, but the email failed — check the Email Log");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend"); }
  };

  return (
    <AdminShell current="/admin/rms">
      <div className="px-6 py-6">
        <h1 className="text-xl font-bold text-white" data-testid="admin-rms-title">Relationship Managers</h1>
        <p className="mt-1 text-sm text-neutral-400">Assign founders to an RM and monitor their check-in activity. RMs only see their own book.</p>

        <FlagAlerts />

        <div className="mt-5 flex gap-2">
          {[["managers", "Relationship Managers"], ["referrals", "Referrals"], ["assessment", "Assessment Review"], ["report", "Activity Report"], ["emails", "Email Log"], ["training", "Training Overview"], ["questions", "Question Board"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
              className={`rounded-full px-4 py-1.5 text-sm font-bold ${tab === v
                ? "bg-[#0A8B85]/20 text-[#2dd4bf] ring-1 ring-[#0A8B85]/40"
                : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"}`}
              data-testid={`rm-tab-${v}`}>{l}</button>
          ))}
        </div>

        {tab === "referrals" && <div className="mt-6"><ReferralPipeline /></div>}
        {tab === "assessment" && <div className="mt-6"><RMAssessmentReview /></div>}
        {tab === "report" && <div className="mt-6"><RMActivityReport /></div>}
        {tab === "emails" && <div className="mt-6"><RMEmailLog /></div>}
        {tab === "training" && <div className="mt-6"><RMTrainingOverview /></div>}
        {tab === "questions" && <div className="mt-6"><RMQuestionBoard /></div>}

        {tab === "managers" && (<>
        <div className="mt-5">
          <AddRMForm onCreated={loadRms} />
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="admin-rms-cards">
          {loading ? <p className="text-sm text-neutral-500">Loading…</p> : rms.length === 0 ? (
            <p className="text-sm text-neutral-500" data-testid="admin-rms-empty">No RMs yet — use "Add RM" above to create one.</p>
          ) : rms.map((r) => (
            <div key={r.user_id} onClick={() => setSelected(r)} role="button"
              className={`relative rounded-2xl border p-4 text-left transition cursor-pointer ${selected?.user_id === r.user_id
                ? "border-[#0A8B85] bg-[#0A8B85]/10" : "border-neutral-800 bg-neutral-950 hover:border-neutral-600"}`}
              data-testid={`admin-rm-card-${r.user_id}`}>
              <div className="absolute right-3 top-3 flex gap-1">
                <button onClick={(e) => { e.stopPropagation(); resendWelcome(r); }}
                  className="rounded-md p-1 text-neutral-500 hover:bg-[#0A8B85]/10 hover:text-[#2dd4bf]"
                  title="Resend welcome email (new temporary password)" data-testid={`admin-rm-resend-${r.user_id}`}>
                  <MailPlus size={14} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); removeRm(r); }}
                  className="rounded-md p-1 text-neutral-500 hover:bg-red-500/10 hover:text-red-400"
                  title="Remove RM" data-testid={`admin-rm-remove-${r.user_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="font-bold text-neutral-100">{r.name}</p>
              <p className="text-[11px] text-neutral-500">{r.email}</p>
              {r.rm_status && (
                <span className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${r.rm_status === "approved" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}
                  data-testid={`admin-rm-status-${r.user_id}`}>
                  {r.rm_status === "approved" ? "Approved" : "Awaiting Review"}
                </span>
              )}
              <div className="mt-3 flex gap-4 text-xs">
                <span className="text-neutral-300"><b className="text-[#2dd4bf]">{r.client_count}</b> clients</span>
                <span className="text-neutral-300"><b className="text-[#2dd4bf]">{r.checkins_30d}</b> check-ins (30d)</span>
                <span className="text-neutral-300"><b className="text-[#2dd4bf]">{r.checkins_total}</b> total</span>
              </div>
              <p className="mt-1 text-[11px] text-neutral-500">Last check-in: {fmt(r.last_checkin_at)}</p>
            </div>
          ))}
        </div>

        {selected && (
          <div className="mt-8">
            <h2 className="text-base font-bold text-white">{selected.name}'s clients</h2>
            <div className="mt-3 max-w-xl">
              <AssignSearch rm={selected} onAssigned={() => { loadClients(selected); loadRms(); }} />
            </div>

            <div className="mt-4 overflow-x-auto rounded-2xl border border-neutral-800">
              <table className="w-full min-w-[1000px] text-left text-sm" data-testid="admin-rm-clients-table">
                <thead className="bg-neutral-900 text-[10px] uppercase tracking-wider text-neutral-400">
                  <tr>
                    <th className="px-3 py-3">Client</th>
                    <th className="px-3 py-3">Company</th>
                    <th className="px-3 py-3 text-center">Stage</th>
                    <th className="px-3 py-3 text-center">CRS</th>
                    <th className="px-3 py-3 text-center">Report</th>
                    <th className="px-3 py-3">Last Check-in</th>
                    <th className="px-3 py-3">Next Due</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3">Services</th>
                    <th className="px-3 py-3">Notes</th>
                    <th className="px-3 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800 bg-neutral-950">
                  {clients.length === 0 ? (
                    <tr><td colSpan={11} className="px-4 py-10 text-center text-neutral-500" data-testid="admin-rm-no-clients">
                      No clients assigned yet — search above to assign founders.
                    </td></tr>
                  ) : clients.map((c) => {
                    const [label, cls] = STATUS_LABELS[c.client_status] || ["—", "text-neutral-500"];
                    return (
                      <Fragment key={c.company_id}>
                        <tr className="align-top" data-testid={`admin-rm-client-${c.company_id}`}>
                          <td className="px-3 py-3">
                            <p className="font-semibold text-neutral-100">{c.client_name}</p>
                            <p className="text-[11px] text-neutral-500">{c.client_email}</p>
                          </td>
                          <td className="px-3 py-3 text-neutral-300">{c.company_name}</td>
                          <td className="px-3 py-3 text-center font-bold text-neutral-100">{c.stage}</td>
                          <td className="px-3 py-3 text-center font-bold text-[#2dd4bf]">{c.crs_score ?? "—"}</td>
                          <td className="px-3 py-3 text-center text-xs">
                            {c.report_generated ? <span className="font-semibold text-emerald-400">Yes</span> : <span className="text-neutral-500">No</span>}
                          </td>
                          <td className="px-3 py-3 text-xs text-neutral-300">{fmt(c.last_checkin_at)}</td>
                          <td className="px-3 py-3 text-xs text-neutral-300">{c.next_checkin_due || "—"}</td>
                          <td className="px-3 py-3">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}>{label}</span>
                            {c.referral_flag && (
                              <p className="mt-1 max-w-[180px] rounded-md bg-amber-500/10 px-1.5 py-1 text-[10px] font-bold text-amber-400"
                                title={c.referral_flag.note}
                                data-testid={`admin-rm-referral-${c.company_id}`}>
                                ⚑ Partner referral · {(c.referral_flag.flagged_at || "").slice(0, 10)}
                              </p>
                            )}
                            {c.status_reason && c.client_status !== "active" && (
                              <p className="mt-1 max-w-[160px] text-[10px] italic text-neutral-500" data-testid={`admin-rm-status-reason-${c.company_id}`}>{c.status_reason}</p>
                            )}
                          </td>
                          <td className="px-3 py-3 min-w-[150px]" data-testid={`admin-rm-services-${c.company_id}`}>
                            {Object.keys(c.services_offered || {}).length === 0 ? (
                              <span className="text-[11px] text-neutral-600">—</span>
                            ) : Object.entries(c.services_offered).map(([k, v]) => (
                              <p key={k} className="text-[10px] text-neutral-300">
                                <span className="font-semibold text-[#2dd4bf]">✓</span> {SERVICE_LABELS[k] || k}
                                <span className="text-neutral-500"> · {v.introduced_at}</span>
                              </p>
                            ))}
                          </td>
                          <td className="px-3 py-3 max-w-[220px] text-xs text-neutral-400">{c.notes || "—"}</td>
                          <td className="px-3 py-3">
                            <button onClick={() => setOpenHistory((h) => ({ ...h, [c.company_id]: !h[c.company_id] }))}
                              className="flex items-center gap-1 rounded-md border border-neutral-700 px-2 py-1 text-[10px] font-bold text-neutral-300 hover:bg-neutral-800"
                              data-testid={`admin-rm-history-toggle-${c.company_id}`}>
                              <History size={12} /> History ({c.checkin_count})
                            </button>
                            <button onClick={() => unassign(c.company_id)}
                              className="mt-1.5 flex items-center gap-1 rounded-md border border-red-500/40 px-2 py-1 text-[10px] font-bold text-red-400 hover:bg-red-500/10"
                              data-testid={`admin-rm-unassign-${c.company_id}`}>
                              <UserMinus size={12} /> Unassign
                            </button>
                          </td>
                        </tr>
                        {openHistory[c.company_id] && (
                          <tr className="bg-neutral-900/40">
                            <td colSpan={11}><HistoryRows companyId={c.company_id} /></td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>)}
      </div>
    </AdminShell>
  );
}
